import React, { useState, useEffect, useRef, useMemo } from "react";
import {
  User,
  GraduationCap,
  Briefcase,
  FileText,
  Upload,
  CheckCircle,
  ArrowRight,
  ArrowLeft,
  Save,
  AlertCircle,
  Info,
  LucideIcon,
} from "lucide-react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import useAuth from "../../contexts/AuthContext";
import { useToast } from "../../common_components/Toast";
import Loader from "../../common_components/Loader";
import { createMasterDataService } from "../../services/masterDataService";
import { AccountDetailsCard } from "../../common_components/Account";
import DynamicSection from "../dynamic-form/components/DynamicSection";
import { validateForm, calculateExperience,calculateExpectedFee } from "../dynamic-form/validators/validators";
import { ApplicationStatus, PaymentStatus } from "../../types/status";

export interface AccountDetails {
  bankName: string;
  branchName: string;
  accountNumber: string;
  ifsc: string;
  qrCodeKey?: string;
  qrCodeUrl?: string;
}

interface StepConfig {
  id: number;
  name: string;
  sectionId: string;
  icon: LucideIcon;
}

interface PaymentState {
  amount: number;
  mode: string;
  transactionId: string;
  utrNumber: string;
  paymentDate: string;
  status: string;
}

interface DeclarationState {
  accepted: boolean;
  acceptedAt: string | null;
  ipAddress: string | null;
}

interface Instruction {
  text: string;
}

interface TotalExperience {
  years: number;
  months: number;
  days: number;
}

interface FormData {
  personalInfo?: Record<string, unknown>;
  qualifications?: Record<string, unknown>;
  experience?: { experienceList?: unknown[] };
  documents?: Record<string, { key?: string }>;
  totalExperience?: TotalExperience;
  [key: string]: unknown;
}

export default function ApplyApplication() {
  const navigate = useNavigate();
  const { recruitmentId, applicationId: routeAppId } = useParams<{
    recruitmentId: string;
    applicationId?: string;
  }>();
  const location = useLocation();
  const { apiRequest, user, isRecruitmentAdmin, isSuperAdmin } = useAuth();
  const isAdmin = isRecruitmentAdmin || isSuperAdmin
  const toast = useToast();
  const masterDataService = useMemo(() => createMasterDataService({ apiRequest }), [apiRequest]);

  const isNonFaculty = location.pathname.includes("non-faculty");
  const apiPrefix = isNonFaculty ? "/non-faculty" : "/faculty";
  const myApplicationsUrl = isNonFaculty ? "/non-faculty/applications" : "/faculty/applications";

  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Record<string, unknown>>({});
  const [isSubmitted, setIsSubmitted] = useState(false);

  const [recruitment, setRecruitment] = useState<Record<string, unknown> | null>(null);
  const [applicationId, setApplicationId] = useState<string | undefined>(routeAppId);
  const [accountDetails, setAccountDetails] = useState<AccountDetails>();
  const [initialInstructions, setInitialInstructions] = useState<Instruction[]>([]);
  const [showInstructions, setShowInstructions] = useState(true);

  // Form State
  const [formData, setFormData] = useState<FormData>({});
  const [payment, setPayment] = useState<PaymentState>({
    amount: 0,
    mode: "OFFLINE",
    transactionId: "",
    utrNumber: "",
    paymentDate: "",
    status: PaymentStatus.PENDING,
  });
  const [declaration, setDeclaration] = useState<DeclarationState>({
    accepted: false,
    acceptedAt: null,
    ipAddress: null,
  });

  const stepsRef = useRef<StepConfig[]>([]);

  useEffect(() => {
    fetchAccountDetails();
    fetchInstructions();
  }, []);

  const fetchInstructions = async () => {
    try {
      const typeLabel = isNonFaculty ? "Non-Faculty Recruitment" : "Faculty Recruitment";
      const res = await masterDataService.getInstructions(typeLabel);
      setInitialInstructions(res.data || []);
    } catch (err) {
      console.error("Failed to load instructions:", err);
    }
  };

  const fetchAccountDetails = async () => {
    try {
      const data = await apiRequest(`/account`, { method: "GET" });
      setAccountDetails(data);
    } catch (err) {
      console.error("Error fetching account details:", err);
    }
  };

  useEffect(() => {
    async function loadData() {
      setLoading(true);
      setError(null);
      try {
        const recRes = await apiRequest(`${apiPrefix}/recruitment/${recruitmentId}`, { method: "GET" });
        const rec = recRes?.recruitment;
        if (!rec) throw new Error("Recruitment details not found");
        setRecruitment(rec);

        stepsRef.current = ((rec.applicationFormSchema as { sections?: Array<{ id: string; title: string }> })?.sections || []).map(
          (sec, idx) => ({
            id: idx + 1,
            name: sec.title,
            sectionId: sec.id,
            icon: getStepIcon(sec.id),
          })
        );

        if (applicationId) {
          const appRes = await apiRequest(`${apiPrefix}/application/${applicationId}`, { method: "GET" });
          const app = appRes?.application;
          if (app) {
            const loadedForm: FormData = app.formData || {};
            if (!loadedForm.qualifications) loadedForm.qualifications = {};
            const quals = loadedForm.qualifications as Record<string, unknown>;
            if (!Array.isArray(quals.qualificationsList)) {
              quals.qualificationsList = [];
            }
            if (!loadedForm.experience) loadedForm.experience = {};
            const exp = loadedForm.experience as Record<string, unknown>;
            if (!Array.isArray(exp.experienceList)) {
              exp.experienceList = [];
            }
            setFormData(loadedForm);
            setPayment(app.payment || { amount: 0, mode: "OFFLINE", transactionId: "", utrNumber: "", paymentDate: "", status: PaymentStatus.PENDING });
            setDeclaration(app.declaration || { accepted: false, acceptedAt: null, ipAddress: null });
            setCurrentStep(app.currentStep || 1);
            if (app.status !== ApplicationStatus.DRAFT) setIsSubmitted(true);
          }
        } else {
          const myAppsRes = await apiRequest(`${apiPrefix}/application/my-applications?recruitmentId=${recruitmentId}&status=${ApplicationStatus.DRAFT}`, { method: "GET" });
          const existingDraft = myAppsRes?.applications?.find(
            (app: any) => app.recruitmentId === recruitmentId && app.status === ApplicationStatus.DRAFT
          );
          if (existingDraft) {
            setApplicationId(existingDraft.applicationId);
            navigate(`${apiPrefix}/application/${recruitmentId}/${existingDraft.applicationId}/edit`, { replace: true });
            return;
          }

          const initialForm: FormData = {};
          ((rec.applicationFormSchema as { sections?: Array<{ id: string }> })?.sections || []).forEach((sec) => {
            initialForm[sec.id] = {};
          });
          if (initialForm.personalInfo) {
            (initialForm.personalInfo as Record<string, unknown>).email = (user as { email?: string })?.email || "";
          }
          if (!initialForm.qualifications) initialForm.qualifications = {};
          (initialForm.qualifications as Record<string, unknown>).qualificationsList = [];
          if (!initialForm.experience) initialForm.experience = {};
          (initialForm.experience as Record<string, unknown>).experienceList = [];
          setFormData(initialForm);
        }
      } catch (err: unknown) {
        console.error("Load failed:", err);
        const e = err as { message?: string };
        setError(e.message || "Failed to load application data.");
      } finally {
        setLoading(false);
      }
    }

    if (recruitmentId) loadData();
  }, [recruitmentId, applicationId]);

  // Sync sameAddress
  useEffect(() => {
    if ((formData.personalInfo as Record<string, unknown>)?.sameAddress) {
      setFormData((prev) => {
        const personal = prev.personalInfo as Record<string, unknown> || {};
        if (JSON.stringify(personal.permanentAddress) !== JSON.stringify(personal.currentAddress)) {
          return {
            ...prev,
            personalInfo: { ...personal, permanentAddress: { ...((personal.currentAddress || {}) as Record<string, unknown>) } },
          };
        }
        return prev;
      });
    }
  }, [
    (formData.personalInfo as Record<string, unknown>)?.currentAddress,
    (formData.personalInfo as Record<string, unknown>)?.sameAddress,
  ]);

  const getStepIcon = (sectionId: string): LucideIcon => {
    switch (sectionId) {
      case "personalInfo": return User;
      case "qualifications": return GraduationCap;
      case "experience": return Briefcase;
      case "documents": return Upload;
      default: return FileText;
    }
  };

  const expectedFee = recruitment ? calculateExpectedFee(
    formData,
    recruitment
  ) : 0;

  const dateStatus = (() => {
    const dates = (recruitment as { applicationDates?: { startDate?: string; endDate?: string } })?.applicationDates;
    if (!dates) return { canApply: true, message: null, isAdminTesting: false };
    const now = new Date();
    const startDate = dates.startDate ? new Date(dates.startDate) : null;
    const endDate = dates.endDate ? new Date(dates.endDate) : null;

    const isUpcoming = startDate ? now < startDate : false;
    if (isUpcoming && startDate && !isAdmin) {
      const formattedStart = startDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
      return {
        canApply: false,
        message: `Applications for this recruitment have not started yet. Applications open on ${formattedStart}.`,
        isAdminTesting: false,
      };
    }
    if (endDate && now > endDate) {
      const formattedEnd = endDate.toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" });
      return {
        canApply: false,
        message: `Application period for this recruitment closed on ${formattedEnd}.`,
        isAdminTesting: false,
      };
    }
    return { canApply: true, message: null, isAdminTesting: isUpcoming && isAdmin };
  })();

  const handleFieldChange = (sectionId: string, fieldId: string, value: unknown) => {
    const prevSection = (formData[sectionId] as Record<string, unknown>) || {};
    if (prevSection[fieldId] === value) return;

    let resetMessage: string | null = null;

    if (sectionId === "personalInfo") {
      if (fieldId === "candidateType" || fieldId === "categoryOfCandidate") {
        const resetFields: string[] = [];
        if (prevSection.department) resetFields.push("Department");
        if (prevSection.postName) resetFields.push("Post Name");
        if (prevSection.categoryOfPost) resetFields.push("Category of Post");

        const triggerLabel = fieldId === "candidateType" ? "Candidate Type" : "Category of Candidate";
        if (resetFields.length > 0) {
          resetMessage = `${resetFields.join(", ")} ${resetFields.length > 1 ? "have" : "has"} been reset due to change in ${triggerLabel}.`;
        }
      } else if (fieldId === "department") {
        const resetFields: string[] = [];
        if (prevSection.postName) resetFields.push("Post Name");
        if (prevSection.categoryOfPost) resetFields.push("Category of Post");

        if (resetFields.length > 0) {
          resetMessage = `${resetFields.join(", ")} ${resetFields.length > 1 ? "have" : "has"} been reset due to change in Department.`;
        }
      } else if (fieldId === "postName") {
        const resetFields: string[] = [];
        if (prevSection.categoryOfPost) resetFields.push("Category of Post");

        if (resetFields.length > 0) {
          resetMessage = `Category of Post has been reset due to change in Post Name.`;
        }
      }
    }

    setFormData((prev) => {
      const updatedSection: Record<string, unknown> = { ...(prev[sectionId] as Record<string, unknown> || {}), [fieldId]: value };
      if (sectionId === "personalInfo") {
        if (fieldId === "candidateType" || fieldId === "categoryOfCandidate") {
          updatedSection.department = "";
          updatedSection.postName = "";
          updatedSection.categoryOfPost = "";
        } else if (fieldId === "department") {
          updatedSection.postName = "";
          updatedSection.categoryOfPost = "";
        } else if (fieldId === "postName") {
          updatedSection.categoryOfPost = "";
        }
      }
      return { ...prev, [sectionId]: updatedSection };
    });

    if (resetMessage) {
      toast.info(resetMessage);
    }
  };

  const handlePaymentChange = (field: keyof PaymentState, value: string | number) => {
    setPayment((prev) => ({ ...prev, [field]: value }));
  };

  const validateCurrentState = (step: number = currentStep, customFormData: FormData | null = null): boolean => {
    if (!recruitment?.applicationFormSchema) return true;
    const schema = recruitment.applicationFormSchema as { sections: unknown[] };
    const sectionsToValidate = schema.sections.slice(0, step);
    const subSchema = { sections: sectionsToValidate };
    const dataToValidate = customFormData || formData;
    const errors = validateForm(subSchema, dataToValidate, recruitment);
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const saveProgress = async (nextStep: number = currentStep, customFormData: FormData | null = null): Promise<boolean> => {
    if (isSubmitted) {
      toast.error("Cannot modify submitted application");
      return false;
    }

    setSaving(true);
    setError(null);
    try {
      const activeData = customFormData || formData;
      const experiences = (activeData.experience?.experienceList || []) as unknown[];
      const totalExp = calculateExperience(experiences);
      const dataToSave: FormData = { ...activeData, totalExperience: totalExp };
      setFormData(dataToSave);

      const computedFee = calculateExpectedFee(formData,recruitment);
      const updatedPayment = { ...payment, amount: computedFee };
      if (computedFee === 0) updatedPayment.status = PaymentStatus.PAID;

      const url = applicationId ? `${apiPrefix}/application/${applicationId}` : `${apiPrefix}/application`;

      const response = await apiRequest(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recruitmentId, formData: dataToSave, payment: updatedPayment, declaration, currentStep: nextStep }),
      });

      const savedApp = response?.application;
      if (savedApp) {
        if (!applicationId && savedApp.applicationId) {
          setApplicationId(savedApp.applicationId);
          navigate(`${apiPrefix}/application/${recruitmentId}/${savedApp.applicationId}/edit`, { replace: true });
        }
        setFormData(savedApp.formData || {});
        setPayment(savedApp.payment || {});
        setDeclaration(savedApp.declaration || {});
      }

      toast.success("Progress saved successfully!");
      return true;
    } catch (err: unknown) {
      console.error("Save progress error:", err);
      const e = err as { message?: string };
      toast.error(e.message || "Failed to save draft progress.");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleNext = async () => {
    if (isSubmitted) {
      setCurrentStep((prev) => prev + 1);
      return;
    }

    const experiences = (formData.experience?.experienceList || []) as unknown[];
    const totalExp = calculateExperience(experiences);
    const updatedFormData: FormData = { ...formData, totalExperience: totalExp };

    const isValid = validateCurrentState(currentStep, updatedFormData);
    if (!isValid) {
      toast.error("Please resolve validation errors before proceeding.");
      return;
    }

    if (activeSection?.id === "documents" && expectedFee > 0) {
      if (!payment.utrNumber || !payment.paymentDate) {
        toast.error("Please enter UTR / Transaction ID and Payment Date.");
        return;
      }
      const docs = (updatedFormData.documents || {}) as Record<string, { key?: string }>;
      if (!docs.paymentCert?.key) {
        toast.error("Please upload payment receipt screenshot.");
        return;
      }
    }

    const saved = await saveProgress(currentStep + 1, updatedFormData);
    if (saved) {
      setCurrentStep((prev) => prev + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleSubmitApplication = async () => {
    if (isSubmitted) return;

    if (!declaration.accepted) {
      toast.error("You must accept the declaration to submit.");
      return;
    }

    const experiences = (formData.experience?.experienceList || []) as unknown[];
    const totalExp = calculateExperience(experiences);
    const updatedFormData: FormData = { ...formData, totalExperience: totalExp };

    const totalValid = validateCurrentState(stepsRef.current.length, updatedFormData);
    if (!totalValid) {
      toast.error("Please complete all sections accurately before submission.");
      return;
    }

    if (expectedFee > 0) {
      if (!payment.utrNumber || !payment.paymentDate) {
        toast.error("UTR/Transaction ID and payment date are required.");
        return;
      }
      if (!updatedFormData.documents?.paymentCert?.key) {
        toast.error("Please upload payment receipt screenshot.");
        return;
      }
    }

    setSaving(true);
    setError(null);
    try {
      await saveProgress(stepsRef.current.length + 1, updatedFormData);

      await apiRequest(`${apiPrefix}/application/${applicationId}/submit`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ recruitmentId, status: ApplicationStatus.SUBMITTED, declaration }),
      });

      toast.success("Application submitted successfully!");
      setIsSubmitted(true);
      navigate(`${myApplicationsUrl}/${recruitmentId}`);
    } catch (err: unknown) {
      console.error("Submission failed:", err);
      const e = err as { message?: string };
      toast.error(e.message || "Failed to submit application.");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Loader />;

  const recruitmentSchema = (recruitment?.applicationFormSchema as { sections?: unknown[] });
  const activeSection = recruitmentSchema?.sections?.[currentStep - 1] as { id: string; title: string;[key: string]: unknown } | undefined;
  const totalStepsCount = stepsRef.current.length + 1;
  const isReviewStep = currentStep === totalStepsCount;

  return (
    <div className="min-h-screen bg-slate-50 p-6 font-sans">
      {/* Instructions Dialog */}
      {showInstructions && initialInstructions.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-2xl border border-gray-200 max-w-2xl w-full p-6 shadow-xl space-y-4"
          >
            <h3 className="text-xl font-bold text-gray-900 border-b pb-3 flex items-center gap-2">
              <Info className="w-5 h-5 text-blue-600" />
              General Instructions
            </h3>
            <div className="max-h-[300px] overflow-y-auto space-y-3 text-sm text-gray-600 pr-2">
              {initialInstructions.map((ins, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100 rounded-xl hover:bg-slate-100 transition-colors">
                  <Info className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                  <p className="leading-relaxed text-gray-700">{ins.text}</p>
                </div>
              ))}
            </div>
            <div className="flex justify-end border-t pt-4">
              <button
                onClick={() => setShowInstructions(false)}
                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-colors"
              >
                I Understand & Proceed
              </button>
            </div>
          </motion.div>
        </div>
      )}

      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">{recruitment?.title as string}</h1>
            <p className="text-sm text-gray-500 mt-1">Application ID: {applicationId || "Draft Generation Pending"}</p>
          </div>
          <div className="flex gap-2">
            {!isSubmitted && (
              <button
                onClick={() => saveProgress(currentStep)}
                disabled={saving}
                className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm font-semibold transition-colors"
              >
                <Save className="w-4 h-4" /> Save Draft
              </button>
            )}
          </div>
        </div>

        {/* Step Indicator */}
        <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm overflow-x-auto">
          <div className="flex items-center justify-between min-w-[700px] md:min-w-0">
            {stepsRef.current.map((step) => {
              const Icon = step.icon;
              const isActive = currentStep === step.id;
              const isDone = currentStep > step.id;

              return (
                <React.Fragment key={step.id}>
                  <div className="flex flex-col items-center flex-1">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${isDone
                        ? "bg-green-600 border-green-600 text-white"
                        : isActive
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-gray-300 text-gray-400"
                        }`}
                    >
                      {isDone ? <CheckCircle className="w-5 h-5" /> : <Icon className="w-4 h-4" />}
                    </div>
                    <span className={`text-[10px] md:text-xs font-semibold mt-2 ${isActive ? "text-blue-600" : "text-gray-500"}`}>
                      {step.name}
                    </span>
                  </div>
                  {step.id < stepsRef.current.length && (
                    <div className={`h-[2px] flex-1 mx-2 ${currentStep > step.id ? "bg-green-500" : "bg-gray-200"}`} />
                  )}
                </React.Fragment>
              );
            })}
            <div className={`h-[2px] w-8 mx-2 ${currentStep > stepsRef.current.length ? "bg-green-500" : "bg-gray-200"}`} />
            <div className="flex flex-col items-center">
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm border-2 transition-all ${isReviewStep ? "bg-blue-600 border-blue-600 text-white" : "bg-white border-gray-300 text-gray-400"
                  }`}
              >
                <CheckCircle className="w-4 h-4" />
              </div>
              <span className={`text-[10px] md:text-xs font-semibold mt-2 ${isReviewStep ? "text-blue-600" : "text-gray-500"}`}>
                Review & Submit
              </span>
            </div>
          </div>
        </div>

        {/* Form Body */}
        <div className="space-y-6">
          {dateStatus.isAdminTesting && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-2xl flex items-start gap-3 text-blue-900 shadow-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-blue-600" />
              <div>
                <h3 className="font-bold text-blue-950">Admin Testing Mode</h3>
                <p className="text-sm text-blue-900 mt-0.5">
                  Applications for this recruitment have not officially started yet. As an Admin, you are allowed to fill and test this application form before the start date.
                </p>
              </div>
            </div>
          )}
          {!dateStatus.canApply && (
            <div className="p-4 bg-amber-50 border border-amber-300 rounded-2xl flex items-start gap-3 text-amber-900 shadow-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-amber-600" />
              <div>
                <h3 className="font-bold text-amber-950">Application Window Inactive</h3>
                <p className="text-sm text-amber-900 mt-0.5">{dateStatus.message}</p>
              </div>
            </div>
          )}
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl flex gap-3 text-red-800 text-sm">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {Object.keys(validationErrors).length > 0 && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-2xl space-y-2 text-sm text-red-800 shadow-xs">
              <div className="flex gap-2 font-bold items-center">
                <AlertCircle className="w-5 h-5 flex-shrink-0" />
                <span>Please correct the following errors before proceeding:</span>
              </div>
              <ul className="list-disc list-inside text-xs text-red-700 pl-6 space-y-1">
                {Object.entries(validationErrors).map(([key, errVal]) => {
                  if (typeof errVal === "string") {
                    return <li key={key}>{errVal}</li>;
                  }
                  if (typeof errVal === "object" && errVal !== null) {
                    return Object.entries(errVal).map(([subKey, subErr]) => {
                      if (typeof subErr === "string") {
                        return <li key={`${key}.${subKey}`}>{subErr}</li>;
                      }
                      if (typeof subErr === "object" && subErr !== null) {
                        return Object.entries(subErr).map(([k, e]) => (
                          <li key={`${key}.${subKey}.${k}`}>{String(e)}</li>
                        ));
                      }
                      return null;
                    });
                  }
                  return null;
                })}
              </ul>
            </div>
          )}

          {!isReviewStep && activeSection && (
            <div className="space-y-6">
              {activeSection.id === "documents" && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
                  <div className="border-b pb-3">
                    <h3 className="text-xl font-bold text-gray-900">Application Fee & Payment Details</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Please scan the QR code or transfer the registration fee to the bank account below, enter your transaction reference details, and then upload your payment receipt below.
                    </p>
                  </div>

                  {expectedFee > 0 ? (
                    <>
                      <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
                        <span className="text-md font-bold text-blue-900">Required Registration Fee:</span>
                        <span className="text-2xl font-black text-blue-950">₹{expectedFee}</span>
                      </div>

                      <AccountDetailsCard accountDetails={accountDetails} />

                      <div className="grid md:grid-cols-2 gap-4 pt-2">
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700">
                            UTR / Transaction ID <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={payment.utrNumber || ""}
                            onChange={(e) => handlePaymentChange("utrNumber", e.target.value)}
                            disabled={isSubmitted || !dateStatus.canApply}
                            placeholder="Enter bank transaction reference number"
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="block text-sm font-semibold text-gray-700">
                            Payment Date <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="date"
                            value={payment.paymentDate ? payment.paymentDate.substring(0, 10) : ""}
                            onChange={(e) => handlePaymentChange("paymentDate", e.target.value)}
                            disabled={isSubmitted || !dateStatus.canApply}
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center justify-between text-green-800">
                      <span className="text-sm font-bold">Fee Exemption Applied: ₹0 Registration Fee required for your category.</span>
                    </div>
                  )}
                </div>
              )}

              <DynamicSection
                section={activeSection}
                sectionData={(formData[activeSection.id] as Record<string, unknown>) || {}}
                onChange={handleFieldChange}
                errors={validationErrors}
                applicationId={applicationId || ""}
                disabled={isSubmitted || !dateStatus.canApply}
                folder={isNonFaculty ? "non-faculty" : "faculty"}
                recruitment={recruitment}
              />
            </div>
          )}

          {isReviewStep && (
            <div className="space-y-8">
              {/* Dynamic Review Sections */}
              {(recruitmentSchema?.sections || []).map((sec) => {
                const section = sec as { id: string; title: string;[key: string]: unknown };
                return (
                  <div key={section.id} className="space-y-4">
                    {section.id === "experience" && formData.totalExperience && (
                      <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex items-center justify-between shadow-xs">
                        <div className="flex items-center gap-3">
                          <Briefcase className="w-5 h-5 text-blue-600" />
                          <div>
                            <span className="text-xs font-bold text-blue-800 uppercase tracking-wider block">Calculated Total Work Experience</span>
                            <span className="text-sm font-semibold text-blue-950 mt-0.5 block">
                              {formData.totalExperience.years} Years, {formData.totalExperience.months} Months, {formData.totalExperience.days} Days
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                    <DynamicSection
                      section={section}
                      sectionData={(formData[section.id] as Record<string, unknown>) || {}}
                      onChange={() => { }}
                      errors={{}}
                      applicationId={applicationId || ""}
                      disabled={true}
                      recruitment={recruitment}
                    />
                  </div>
                );
              })}

              {/* Payment Details Card */}
              {expectedFee > 0 && (
                <div className="bg-white rounded-2xl border border-gray-200 p-6 shadow-sm space-y-6">
                  <div className="border-b pb-3">
                    <h3 className="text-xl font-bold text-gray-900">Payment Verification</h3>
                    <p className="text-sm text-gray-500 mt-1">This application requires a registration fee payment.</p>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-5 flex items-center justify-between">
                    <span className="text-md font-bold text-blue-900">Required Registration Fee:</span>
                    <span className="text-2xl font-black text-blue-950">₹{expectedFee}</span>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-gray-700">UTR / Transaction ID *</label>
                      <input
                        type="text"
                        value={payment.utrNumber || ""}
                        onChange={(e) => handlePaymentChange("utrNumber", e.target.value)}
                        disabled={isSubmitted}
                        placeholder="Enter bank transaction reference number"
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="block text-sm font-semibold text-gray-700">Payment Date *</label>
                      <input
                        type="date"
                        value={payment.paymentDate ? payment.paymentDate.substring(0, 10) : ""}
                        onChange={(e) => handlePaymentChange("paymentDate", e.target.value)}
                        disabled={isSubmitted}
                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Declaration */}
              <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 space-y-4">
                <h3 className="text-lg font-bold text-amber-900">Declaration by Candidate</h3>
                <p className="text-sm text-amber-950 leading-relaxed">
                  I hereby declare that the above information is true, complete and correct to the best of my knowledge and belief.
                  I have not suppressed any material, fact or factual information. I understand that my candidature is liable to be
                  rejected in the event of any misstatement/discrepancy being detected before or after my appointment.
                </p>
                <label className="flex items-start gap-3 cursor-pointer pt-2">
                  <input
                    type="checkbox"
                    checked={declaration.accepted}
                    disabled={isSubmitted}
                    onChange={(e) => setDeclaration((prev) => ({ ...prev, accepted: e.target.checked }))}
                    className="w-5 h-5 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mt-0.5"
                  />
                  <span className="text-sm font-bold text-amber-900">
                    I accept the above declaration and confirm that all information provided is accurate.
                  </span>
                </label>
              </div>

              {/* Verified Warning */}
              {expectedFee > 0 && payment.status !== PaymentStatus.PAID && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-2xl flex gap-3 text-yellow-800 text-sm">
                  <Info className="w-5 h-5 flex-shrink-0" />
                  <span>
                    <strong>Offline Payment Details Received:</strong> Your payment reference details will be recorded.
                    Upon saving or submitting, your offline payment will be marked as registered for verification.
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Navigation Controls */}
          <div className="flex items-center justify-between pt-6 border-t border-gray-200 bg-white p-5 rounded-2xl border shadow-sm">
            <button
              onClick={handleBack}
              disabled={currentStep === 1 || saving}
              className="flex items-center gap-1.5 px-4 py-2 border border-gray-300 text-gray-700 bg-white hover:bg-gray-100 rounded-lg text-sm font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ArrowLeft className="w-4 h-4" /> Previous
            </button>

            {!isReviewStep ? (
              <button
                onClick={handleNext}
                disabled={saving || !dateStatus.canApply}
                className="flex items-center gap-1.5 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-semibold transition-all disabled:opacity-60"
              >
                Next Step <ArrowRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmitApplication}
                disabled={saving || isSubmitted || !declaration.accepted || !dateStatus.canApply || (expectedFee > 0 && payment.mode !== "OFFLINE" && payment.status !== PaymentStatus.PAID)}
                className="flex items-center gap-1.5 px-6 py-2.5 bg-green-600 hover:bg-green-700 text-white rounded-lg text-sm font-bold transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Submit Application <CheckCircle className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
